/**
 * Custom QWC2 search providers.
 *
 * MML (Maanmittauslaitos) geocoding providers use the Sweco proxy for the
 * NLS Finland geocoding API (Pelias based):
 * https://www.maanmittauslaitos.fi/kartat-ja-paikkatieto/aineistot-ja-rajapinnat/paikkatietojen-rajapintapalvelut/geokoodauspalvelu
 *
 * Providers are enabled per theme in themesConfig.json, e.g.:
 *   "searchProviders": [
 *       { "provider": "mmlAddress", "params": { "kuntarajaus": ["Harjavalta", "Kangasala"] } }
 *   ]
 * The "params" object is available as searchParams.cfgParams.
 *
 * Supported params for MML providers (all optional):
 *   kuntarajaus  Municipality name or list of names. Results are grouped per municipality
 *                and results from other municipalities are left out.
 *                Not used by mmlCadastralUnits (a property ID is unique in Finland).
 *   bbox         [minx, miny, maxx, maxy]. Only results inside this extent are shown.
 *   bboxCrs      CRS of "bbox", default EPSG:3067.
 *   size         Max number of results per API request, default 20.
 */

import CoordinatesUtils from 'qwc2/utils/CoordinatesUtils';
import VectorLayerUtils from 'qwc2/utils/VectorLayerUtils';

const MML_GEOCODE_URL = "https://kartat.sweco.fi/geocodemml";
const MML_CRS = "EPSG:3067";
const MML_DEFAULT_SIZE = 20;

/* ************************************************************************ **/
// Shared helpers

/**
 * Returns the value as a list: "a" -> ["a"], ["a", "b"] -> ["a", "b"], undefined -> [].
 */
function toList(value) {
    if (!value) {
        return [];
    }
    return Array.isArray(value) ? value : [value];
}

/**
 * Returns the configured bbox in EPSG:3067 (the CRS of MML results), or null.
 */
function mmlFilterBbox(cfgParams) {
    if (!Array.isArray(cfgParams.bbox) || cfgParams.bbox.length !== 4) {
        return null;
    }
    const bboxCrs = cfgParams.bboxCrs || MML_CRS;
    return bboxCrs === MML_CRS ? cfgParams.bbox : CoordinatesUtils.reprojectBbox(cfgParams.bbox, bboxCrs, MML_CRS);
}

/**
 * True if the point [x, y] is inside the bbox. A missing bbox accepts every point.
 */
function inBbox(coords, bbox) {
    return !bbox || (coords[0] >= bbox[0] && coords[0] <= bbox[2] && coords[1] >= bbox[1] && coords[1] <= bbox[3]);
}

/* ************************************************************************ **/
// Shared MML helpers

/**
 * Language for the MML API. The API supports fi, sv and en.
 */
function mmlLang(searchParams) {
    const lang = (searchParams.lang || "fi").substring(0, 2);
    return ["fi", "sv", "en"].includes(lang) ? lang : "fi";
}

/**
 * Sends one request to the MML API and returns a promise with the list of features.
 * On error an empty list is returned, so one failed request does not break the others.
 */
function mmlRequest(axios, params) {
    return axios.get(MML_GEOCODE_URL, {params})
        .then(response => response.data.features || [])
        .catch(() => []);
}

/**
 * Converts a GeoJSON feature from the MML API into a QWC2 search result item.
 */
function mmlFeatureToItem(feature, id, text) {
    const coords = feature.geometry.coordinates;
    const bbox = (feature.bbox && feature.bbox.length === 4)
        ? feature.bbox.slice(0)
        : [coords[0], coords[1], coords[0], coords[1]];
    return {
        id: id,
        text: text,
        x: coords[0],
        y: coords[1],
        crs: MML_CRS,
        bbox: bbox
    };
}

/**
 * Creates a QWC2 search provider for one MML geocoding source.
 *
 * @param {Object} options
 * @param {string} options.id           Prefix for result/group ids
 * @param {string} options.label        Provider label shown in the search filter
 * @param {string} options.title        Result group title
 * @param {string} options.source       MML "sources" parameter
 * @param {boolean} options.useMunicipality  Add the municipality name to the query text
 * @param {Function} options.formatText Builds the result text from feature properties
 */
function createMmlProvider(options) {
    function onSearch(text, searchParams, callback, axios) {
        const cfgParams = searchParams.cfgParams || {};
        const municipalities = toList(cfgParams.kuntarajaus);
        const bbox = mmlFilterBbox(cfgParams);

        const baseParams = {
            sources: options.source,
            crs: MML_CRS,
            lang: mmlLang(searchParams),
            size: cfgParams.size || MML_DEFAULT_SIZE
        };

        let requests;
        if (options.useMunicipality && municipalities.length > 0) {
            // MML uses a municipality name in the query text as a filter. With several names
            // in one query the API combines them with AND (no results), so one request is
            // sent per municipality.
            requests = municipalities.map(municipality =>
                mmlRequest(axios, {...baseParams, text: text + " " + municipality})
                    .then(features => ({municipality, features}))
            );
        } else if (!options.useMunicipality) {
            // One request without municipality filter (e.g. property ID, which is unique in
            // Finland). Results are grouped by the municipality they belong to.
            requests = [mmlRequest(axios, {...baseParams, text: text}).then(features => {
                const groups = new Map();
                features.forEach(feature => {
                    const municipality = feature.properties["label:municipality"] || null;
                    groups.set(municipality, [...(groups.get(municipality) || []), feature]);
                });
                return [...groups].map(([municipality, groupFeatures]) => ({municipality, features: groupFeatures}));
            })];
        } else {
            // No municipality filter: one request, one group.
            requests = [mmlRequest(axios, {...baseParams, text: text}).then(features => ({municipality: null, features}))];
        }

        Promise.all(requests).then(responses => {
            // One group per municipality, like separate provider entries in the old config
            const results = responses.flat().map(({municipality, features}) => {
                const groupId = options.id + (municipality ? "_" + municipality : "");
                return {
                    id: groupId + "group",
                    title: options.title + (municipality ? " (" + municipality + ")" : ""),
                    items: features
                        .filter(feature => inBbox(feature.geometry.coordinates, bbox))
                        .map((feature, idx) => mmlFeatureToItem(feature, groupId + idx, options.formatText(feature.properties)))
                };
            }).filter(group => group.items.length > 0);
            callback({results});
        });
    }

    return {
        label: options.label,
        onSearch: onSearch,
        getResultGeometry: mmlResultGeometry
    };
}

/**
 * All coordinates are already in the search response, so no extra request is needed.
 */
function mmlResultGeometry(resultItem, callback) {
    callback({
        geometry: {
            type: "Point",
            coordinates: [resultItem.x, resultItem.y]
        },
        crs: MML_CRS,
        hidemarker: false
    });
}

/* ************************************************************************ **/
// Layer feature search from an OGC API Features (OAPIF) endpoint, GeoJSON output
// https://ogcapi.ogc.org/features/
//
// Searches features of one collection by text. Configured in themesConfig.json, e.g.:
//   {
//       "provider": "oapifFeatures",
//       "label": "Liikennepaikat",
//       "params": {
//           "url": "https://avoinapi.vaylapilvi.fi/vaylatiedot/ogc/features/v1",
//           "collection": "ratatiedot:railway_traffic_operating_point",
//           "searchFields": ["name", "operational_point_abbreviation"],
//           "resultTitle": "{name} ({municipality})"
//       }
//   }
//
// Params:
//   url           OAPIF landing page URL, without /collections (required)
//   collection    Collection id (required)
//   searchFields  Attribute fields matched against the search text (required)
//   resultTitle   Result text template, {field} is replaced by the attribute value.
//                 Default: values of searchFields.
//   title         Result group title, default: collection
//   crs           CRS of the returned geometries, default EPSG:3067
//   limit         Max number of results, default 20
//   filter        Extra filter combined with AND, e.g. "municipality = 'Oulu'"
//   filterLang    "ecql-text" (default, GeoServer, case-insensitive ILIKE) or
//                 "cql2-text" (OGC standard, case-sensitive LIKE)
//   bbox          [minx, miny, maxx, maxy], only features inside are returned (server side)
//   bboxCrs       CRS of "bbox", default: crs
//   auth          Basic auth: {"username": "...", "password": "..."}.
//                 NOTE: themes.json is public, so use only a read-only account.

const OAPIF_DEFAULT_CRS = "EPSG:3067";
const OAPIF_DEFAULT_LIMIT = 20;

/**
 * "EPSG:3067" -> "http://www.opengis.net/def/crs/EPSG/0/3067" (CRS format used by OAPIF)
 */
function oapifCrsUri(crs) {
    return "http://www.opengis.net/def/crs/EPSG/0/" + crs.split(":")[1];
}

/**
 * Builds the filter: (field1 ILIKE '%text%' OR field2 ILIKE '%text%') AND (filter)
 */
function oapifFilter(text, cfgParams) {
    const operator = cfgParams.filterLang === "cql2-text" ? " LIKE " : " ILIKE ";
    // Single quotes are escaped by doubling them
    const value = text.replace(/'/g, "''");
    const textFilter = toList(cfgParams.searchFields)
        .map(field => field + operator + "'%" + value + "%'")
        .join(" OR ");
    return cfgParams.filter ? "(" + textFilter + ") AND (" + cfgParams.filter + ")" : textFilter;
}

/**
 * Builds the result text from the resultTitle template, e.g. "{name} ({municipality})".
 */
function oapifResultText(properties, cfgParams) {
    if (cfgParams.resultTitle) {
        return cfgParams.resultTitle.replace(/{([^}]+)}/g, (match, field) => properties[field] ?? "");
    }
    return toList(cfgParams.searchFields).map(field => properties[field]).filter(Boolean).join(", ");
}

function oapifFeaturesSearch(text, searchParams, callback, axios) {
    const cfgParams = searchParams.cfgParams || {};
    if (!cfgParams.url || !cfgParams.collection || toList(cfgParams.searchFields).length === 0) {
        // eslint-disable-next-line no-console
        console.warn("oapifFeatures: url, collection and searchFields are required");
        callback({results: []});
        return;
    }
    const crs = cfgParams.crs || OAPIF_DEFAULT_CRS;
    const url = cfgParams.url.replace(/\/$/, "") + "/collections/" + cfgParams.collection + "/items";

    const params = {
        f: "application/geo+json",
        limit: cfgParams.limit || OAPIF_DEFAULT_LIMIT,
        crs: oapifCrsUri(crs),
        filter: oapifFilter(text, cfgParams),
        "filter-lang": cfgParams.filterLang || "ecql-text"
    };
    if (Array.isArray(cfgParams.bbox) && cfgParams.bbox.length === 4) {
        params.bbox = cfgParams.bbox.join(",");
        params["bbox-crs"] = oapifCrsUri(cfgParams.bboxCrs || crs);
    }
    const requestConfig = {params};
    if (cfgParams.auth) {
        requestConfig.auth = cfgParams.auth;
    }

    axios.get(url, requestConfig).then(response => {
        const items = (response.data.features || [])
            .filter(feature => feature.geometry)
            .map((feature, idx) => {
                const bbox = VectorLayerUtils.computeFeatureBBox(feature);
                return {
                    id: "oapif." + cfgParams.collection + "." + (feature.id ?? idx),
                    text: oapifResultText(feature.properties || {}, cfgParams),
                    x: 0.5 * (bbox[0] + bbox[2]),
                    y: 0.5 * (bbox[1] + bbox[3]),
                    crs: crs,
                    bbox: bbox,
                    // Full geometry, so the whole feature (line/polygon) is highlighted on the map
                    geometry: feature.geometry
                };
            });
        callback({results: items.length > 0 ? [{
            id: "oapif." + cfgParams.collection,
            title: cfgParams.title || cfgParams.collection,
            items: items
        }] : []});
    }).catch(() => {
        callback({results: []});
    });
}

/**
 * The geometry is already in the search result, so no extra request is needed.
 */
function oapifFeaturesResultGeometry(resultItem, callback) {
    callback({
        geometry: resultItem.geometry,
        crs: resultItem.crs,
        hidemarker: resultItem.geometry.type !== "Point"
    });
}

/* ************************************************************************ **/

window.QWC2SearchProviders = {
    // Place name search (paikannimihaku)
    mmlGeographicNames: createMmlProvider({
        id: "mmlGeographicNames",
        label: "Paikannimellä",
        title: "Paikannimihaku",
        source: "geographic-names",
        useMunicipality: true,
        formatText: props => props.label + ", " + props["label:municipality"]
    }),
    // Address search (osoitehaku)
    mmlAddress: createMmlProvider({
        id: "mmlAddress",
        label: "Osoitteella",
        title: "Osoitehaku",
        source: "interpolated-road-addresses",
        useMunicipality: true,
        formatText: props => props.katunimi + " " + props.katunumero + ", " + props["label:municipality"]
    }),
    // Property ID search (kiinteistötunnushaku). The property ID already contains
    // the municipality code, so "kuntarajaus" is not used: results from any municipality
    // are shown, grouped by their municipality.
    mmlCadastralUnits: createMmlProvider({
        id: "mmlCadastralUnits",
        label: "Kiinteistötunnuksella",
        title: "Kiinteistötunnushaku",
        source: "cadastral-units",
        useMunicipality: false,
        formatText: props => props.label + ", " + props["label:municipality"]
    }),
    // OGC API Features layer search, collection and fields are set in themesConfig.json params
    oapifFeatures: {
        label: "OGC API Features",
        onSearch: oapifFeaturesSearch,
        getResultGeometry: oapifFeaturesResultGeometry
    }
};
